import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  Eye,
  Globe,
  Monitor,
  RefreshCw,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TEAL = "#1F6F78";
const SAGE = "#3B6B4A";
const SAND = "#B7791F";
const PLUM = "#6B5CA5";
const CLAY = "#8A5A2E";
const SLATE = "#4B5A60";
const PIE_COLORS = [TEAL, SAGE, SAND, PLUM, CLAY, SLATE];

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

function Panel({ title, subtitle, children, testId, action }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-[#E3DED4] bg-white p-4 sm:p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-bold text-[#1F2A2E]">{title}</h3>
          {subtitle && <p className="text-xs text-[#7A868B] mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Stat({ label, value, sub, icon: Icon, trend, testId }) {
  const up = trend > 0;
  const down = trend < 0;
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-[#E3DED4] bg-white p-4 transition-transform duration-150 hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#7A868B]">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-[#1F6F78]" />}
      </div>
      <p className="font-display text-3xl font-bold text-[#1F2A2E] mt-2">{value}</p>
      <div className="mt-1 flex items-center gap-1.5 text-xs">
        {(up || down) && (
          <span
            className={`inline-flex items-center gap-0.5 font-semibold ${
              up ? "text-[#14603C]" : "text-[#B42318]"
            }`}
          >
            {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
        {sub && <span className="text-[#7A868B]">{sub}</span>}
      </div>
    </div>
  );
}

const tooltipStyle = {
  contentStyle: {
    borderRadius: 12,
    border: "1px solid #E3DED4",
    fontSize: 12,
    boxShadow: "0 8px 24px rgba(31,42,46,0.08)",
  },
  labelStyle: { color: "#1F2A2E", fontWeight: 700 },
};

// Hoisted so Recharts' shallow prop comparison does not see a new object on
// every render of the panel.
const LEGEND_STYLE = { fontSize: 12 };

function EmptyState({ children }) {
  return (
    <div
      data-testid="traffic-empty-state"
      className="h-[220px] grid place-items-center rounded-xl border border-dashed border-[#E3DED4] bg-[#FBF9F5] text-center px-6"
    >
      <div>
        <Globe className="w-6 h-6 text-[#B9C2C0] mx-auto" />
        <p className="mt-2 text-sm font-semibold text-[#4B5A60]">No visits recorded yet</p>
        <p className="text-xs text-[#7A868B] mt-1">{children}</p>
      </div>
    </div>
  );
}

export default function TrafficPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/traffic?days=${d}`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => {
    const t = setInterval(() => load(days), 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const t = data?.totals;
  const hasData = (t?.views_all_time || 0) > 0;

  const trend = useMemo(() => {
    if (!t) return 0;
    if (!t.views_yesterday) return t.views_today > 0 ? 100 : 0;
    return Math.round(((t.views_today - t.views_yesterday) / t.views_yesterday) * 100);
  }, [t]);

  return (
    <div className="mt-6 space-y-5" data-testid="admin-traffic">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">Website traffic</h2>
          <p className="text-sm text-[#4B5A60]">
            First-party, cookie-free visitor analytics. Auto-refreshes every minute.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border-2 border-[#E3DED4] bg-white overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`traffic-range-${r.days}`}
                className={`px-3 py-2 text-sm font-semibold transition-colors duration-150 ${
                  days === r.days
                    ? "bg-[#F2FAFA] text-[#1F6F78]"
                    : "text-[#4B5A60] hover:bg-[#F7F4EF]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => load(days)}
            data-testid="traffic-refresh-btn"
            className="border-[#E3DED4] text-[#1F6F78]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          testId="traffic-kpi-online"
          label="Online now"
          value={t?.online_now ?? 0}
          sub="active in last 5 min"
          icon={Users}
        />
        <Stat
          testId="traffic-kpi-today"
          label="Visitors today"
          value={t?.visitors_today ?? 0}
          sub={`${t?.views_today ?? 0} page views`}
          trend={trend}
          icon={Eye}
        />
        <Stat
          testId="traffic-kpi-window-visitors"
          label={`Visitors (${days}d)`}
          value={t?.visitors_window ?? 0}
          sub={`${t?.new_visitors_window ?? 0} new / ${t?.returning_visitors_window ?? 0} returning`}
          icon={Users}
        />
        <Stat
          testId="traffic-kpi-window-views"
          label={`Page views (${days}d)`}
          value={t?.views_window ?? 0}
          sub={`${t?.pages_per_session ?? 0} pages per visit`}
          icon={Eye}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="traffic-kpi-7d" label="Visitors (7d)" value={t?.visitors_7d ?? 0} sub={`${t?.views_7d ?? 0} views`} />
        <Stat testId="traffic-kpi-30d" label="Visitors (30d)" value={t?.visitors_30d ?? 0} sub={`${t?.views_30d ?? 0} views`} />
        <Stat
          testId="traffic-kpi-alltime"
          label="All-time visitors"
          value={t?.visitors_all_time ?? 0}
          sub={`${t?.views_all_time ?? 0} views total`}
        />
        <Stat
          testId="traffic-kpi-bounce"
          label="Single-page visits"
          value={`${t?.bounce_rate ?? 0}%`}
          sub={`${t?.sessions_window ?? 0} visits in range`}
        />
      </div>

      {/* Visitors over time */}
      <Panel
        title="Visitors & page views over time"
        subtitle={`Daily totals for the last ${days} days`}
        testId="traffic-chart-series"
      >
        {hasData ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={TEAL} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SAGE} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={SAGE} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#7A868B" }}
                interval="preserveStartEnd"
                minTickGap={18}
              />
              <YAxis tick={{ fontSize: 11, fill: "#7A868B" }} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Area
                type="monotone"
                dataKey="views"
                name="Page views"
                stroke={SAGE}
                strokeWidth={2}
                fill="url(#gViews)"
              />
              <Area
                type="monotone"
                dataKey="visitors"
                name="Unique visitors"
                stroke={TEAL}
                strokeWidth={2.5}
                fill="url(#gVisitors)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState>
            Share your site link — every visit is counted here automatically.
          </EmptyState>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Most visited pages" subtitle="Page views in the selected range" testId="traffic-chart-pages">
          {data?.top_pages?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(220, data.top_pages.length * 34)}>
              <BarChart
                data={data.top_pages}
                layout="vertical"
                margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#7A868B" }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={130}
                  tick={{ fontSize: 11, fill: "#4B5A60" }}
                />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="views" name="Views" fill={TEAL} radius={[0, 6, 6, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>Page-level breakdown appears after the first visits.</EmptyState>
          )}
        </Panel>

        <Panel title="Where visitors come from" subtitle="Referral source" testId="traffic-chart-sources">
          {data?.sources?.length ? (
            <div className="space-y-2" data-testid="traffic-sources-list">
              {data.sources.map((s, i) => {
                const max = data.sources[0].views || 1;
                return (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 truncate text-sm text-[#1F2A2E]">{s.label}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-[#F0EDE6] overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.max(4, (s.views / max) * 100)}%`,
                          backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                        }}
                      />
                    </div>
                    <span className="w-24 text-right text-xs text-[#4B5A60]">
                      {s.views} views · {s.visitors} ppl
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState>We will show Google, social and direct traffic here.</EmptyState>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Devices" subtitle="Share of page views" testId="traffic-chart-devices">
          {data?.devices?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={data.devices}
                  dataKey="views"
                  nameKey="label"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={3}
                >
                  {data.devices.map((d, i) => (
                    <Cell key={d.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>Mobile vs desktop split shows up here.</EmptyState>
          )}
        </Panel>

        <Panel title="Browsers" subtitle="Top browsers used" testId="traffic-chart-browsers">
          {data?.browsers?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.browsers} margin={{ top: 0, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7A868B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#7A868B" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="views" name="Views" fill={PLUM} radius={[6, 6, 0, 0]} barSize={26} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>Browser mix appears after the first visits.</EmptyState>
          )}
        </Panel>

        <Panel title="Busiest hours" subtitle="Page views by hour (last 7 days, UTC)" testId="traffic-chart-hourly">
          {hasData ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.hourly} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "#7A868B" }}
                  interval={3}
                />
                <YAxis tick={{ fontSize: 11, fill: "#7A868B" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Line
                  type="monotone"
                  dataKey="views"
                  name="Views"
                  stroke={SAND}
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>Peak visiting hours will appear here.</EmptyState>
          )}
        </Panel>
      </div>

      <Panel title="Live visit feed" subtitle="Most recent page views" testId="traffic-recent">
        {data?.recent?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="traffic-recent-table">
              <thead className="bg-[#F7F4EF] text-left">
                <tr>
                  {["When", "Page", "Source", "Device", "Browser", "Visitor"].map((h) => (
                    <th key={h} className="px-3 py-2 font-bold text-[#4B5A60] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DED4]">
                {data.recent.map((r, i) => (
                  <tr key={`${r.ts}-${i}`}>
                    <td className="px-3 py-2 text-[#4B5A60] whitespace-nowrap">
                      {new Date(r.ts).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-semibold text-[#1F2A2E]">{r.path}</td>
                    <td className="px-3 py-2 text-[#4B5A60]">{r.source}</td>
                    <td className="px-3 py-2 text-[#4B5A60] capitalize">
                      <span className="inline-flex items-center gap-1">
                        <Monitor className="w-3.5 h-3.5 text-[#7A868B]" />
                        {r.device}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[#4B5A60]">{r.browser}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          r.is_new_visitor
                            ? "bg-[#E4F3EA] text-[#14603C]"
                            : "bg-[#F0EDE6] text-[#4B5A60]"
                        }`}
                      >
                        {r.is_new_visitor ? "New" : "Returning"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState>Visits show up here within seconds of someone opening the site.</EmptyState>
        )}
      </Panel>
    </div>
  );
}
